if (!customElements.get('installment-plans')) {
  class InstallmentPlans extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-installment-slider]');
      this.modal = this.querySelector('[data-installment-modal]');
      this.openButton = this.querySelector('[data-installment-open]');
      this.previousButton = this.querySelector('[data-installment-prev]');
      this.nextButton = this.querySelector('[data-installment-next]');
      this.closeButtons = this.querySelectorAll('[data-installment-close]');

      this.baseVariantPrice = Number(this.dataset.currentPrice || 0);
      this.lastAppliedPrice = this.baseVariantPrice;

      this.openButton?.addEventListener('click', () => this.openModal());
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));
      this.previousButton?.addEventListener('click', () => this.scroll(-1));
      this.nextButton?.addEventListener('click', () => this.scroll(1));

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleExternalPriceUpdate = this.handleExternalPriceUpdate.bind(this);
      document.addEventListener('keydown', this.handleKeydown);

      this.productRoot = this.closest('.js-product') || this.closest('[data-product]') || this.parentElement;
      if (this.productRoot) {
        this.productRoot.addEventListener('on:variant:change', this.handleVariantChange);
        this.observeExternalDiscountPrice();
      }

      // Discount Mixer is configured with a delayed update in this store.
      // Recheck several times so the installment cards always follow the final
      // customer-facing discounted price, not the original Shopify variant price.
      [50, 250, 550, 900, 1500, 2500, 4000].forEach((delay) => {
        window.setTimeout(() => this.syncFromRenderedPrice(), delay);
      });

      // Harmless generic hooks: MutationObserver remains the main integration,
      // but these also cover apps/themes that dispatch a custom price-update event.
      ['discount-mixer:updated', 'dxmixer:updated', 'price:updated', 'product:price-change'].forEach((eventName) => {
        document.addEventListener(eventName, this.handleExternalPriceUpdate);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      if (this.productRoot) this.productRoot.removeEventListener('on:variant:change', this.handleVariantChange);
      ['discount-mixer:updated', 'dxmixer:updated', 'price:updated', 'product:price-change'].forEach((eventName) => {
        document.removeEventListener(eventName, this.handleExternalPriceUpdate);
      });
      this.priceObserver?.disconnect();
      if (this.syncTimer) window.clearTimeout(this.syncTimer);
    }

    scroll(direction) {
      if (!this.slider) return;
      const card = this.slider.querySelector('[data-installment-card]');
      const amount = card ? card.getBoundingClientRect().width + 10 : 160;
      this.slider.scrollBy({ left: direction * amount * 2, behavior: 'smooth' });
    }

    openModal() {
      if (!this.modal) return;
      this.modal.hidden = false;
      document.body.classList.add('installment-modal-open');
      this.modal.querySelector('.installment-plans__close')?.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('installment-modal-open');
      this.openButton?.focus();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
    }

    handleExternalPriceUpdate() {
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => this.syncFromRenderedPrice(), 50);
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (variant && typeof variant.price === 'number') {
        this.baseVariantPrice = variant.price;
        this.dataset.currentPrice = variant.price;
        this.lastAppliedPrice = variant.price;
        this.updateCards(variant.price);
      }

      // Discount Mixer can overwrite/insert the visible price after the variant event.
      [50, 250, 550, 900, 1500, 2500].forEach((delay) => {
        window.setTimeout(() => this.syncFromRenderedPrice(), delay);
      });
    }

    observeExternalDiscountPrice() {
      if (!this.productRoot || typeof MutationObserver === 'undefined') return;

      this.priceObserver = new MutationObserver((mutations) => {
        const externalPriceMutation = mutations.some((mutation) => {
          const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
          return target && !target.closest('installment-plans');
        });
        if (!externalPriceMutation) return;

        window.clearTimeout(this.syncTimer);
        this.syncTimer = window.setTimeout(() => this.syncFromRenderedPrice(), 60);
      });

      this.priceObserver.observe(this.productRoot, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'data-price', 'data-discounted-price', 'data-sale-price']
      });
    }

    syncFromRenderedPrice() {
      const detected = this.detectCurrentCustomerPrice();
      if (!detected || detected <= 0) return;
      if (Math.abs(Number(this.lastAppliedPrice || 0) - detected) < 1) return;

      this.lastAppliedPrice = detected;
      this.dataset.currentPrice = detected;
      this.updateCards(detected);
    }

    detectCurrentCustomerPrice() {
      if (!this.productRoot) return Number(this.dataset.currentPrice || 0);

      const base = Number(this.baseVariantPrice || this.dataset.currentPrice || 0);
      const candidates = [];

      // Explicit Discount Mixer / Dxmixer candidates first.
      const appSelectors = [
        '[class*="discount-mixer" i]',
        '[id*="discount-mixer" i]',
        '[class*="discountmixer" i]',
        '[id*="discountmixer" i]',
        '[class*="dxmixer" i]',
        '[id*="dxmixer" i]',
        '[data-discount-mixer-price]',
        '[data-dxmixer-price]',
        '[data-discounted-price]',
        '[data-sale-price]',
        '[class*="discounted-price" i]',
        '[class*="discount_price" i]',
        '[class*="sale-price" i]'
      ];

      this.collectCandidatePrices(appSelectors, candidates, base, true);

      // Theme's normal customer-facing price. This also catches Shopify sale prices.
      const themeSelectors = [
        '.js-product-price .price__current',
        '.product-info__price .price__current',
        '.price--on-sale .price__current',
        '[data-product-price]',
        '.price__current'
      ];
      this.collectCandidatePrices(themeSelectors, candidates, base, false);

      // Last-resort scan for a visible DOM node whose attributes identify it as a
      // price/discount element. This makes the integration resilient to app markup changes.
      this.productRoot.querySelectorAll('*').forEach((node) => {
        if (node.closest('installment-plans')) return;
        if (!this.isVisible(node)) return;
        const marker = `${node.id || ''} ${node.className || ''} ${Array.from(node.attributes || []).map((a) => `${a.name}=${a.value}`).join(' ')}`.toLowerCase();
        if (!/(dxmixer|discount.?mixer|discounted.?price|sale.?price|product.?price)/i.test(marker)) return;
        this.extractPrices(node.textContent || '').forEach((value) => this.pushCandidate(candidates, value, base, true));
      });

      if (!candidates.length) return base;

      // A discount should never make the customer price higher than the base variant price.
      // Pick the lowest credible visible price so original/compare-at prices are ignored.
      return Math.min(...candidates);
    }

    collectCandidatePrices(selectors, output, base, appSpecific) {
      selectors.forEach((selector) => {
        let nodes = [];
        try {
          nodes = this.productRoot.querySelectorAll(selector);
        } catch (error) {
          return;
        }
        nodes.forEach((node) => {
          if (node.closest('installment-plans')) return;
          if (!this.isVisible(node)) return;

          // Prefer numeric data attributes when apps expose them.
          ['data-discount-mixer-price', 'data-dxmixer-price', 'data-discounted-price', 'data-sale-price', 'data-price'].forEach((attr) => {
            if (!node.hasAttribute?.(attr)) return;
            const raw = node.getAttribute(attr);
            const values = this.extractPrices(raw || '');
            values.forEach((value) => this.pushCandidate(output, value, base, appSpecific));
          });

          this.extractPrices(node.textContent || '').forEach((value) => this.pushCandidate(output, value, base, appSpecific));
        });
      });
    }

    pushCandidate(output, value, base, appSpecific) {
      if (!value || value <= 0) return;

      // If the source has a plain integer data-price, some apps store cents.
      // Detect that safely relative to the known Shopify variant price.
      let cents = value;
      if (base > 0 && value > base * 5 && value / 100 <= base * 1.1) cents = Math.round(value / 100);

      // Ignore unrelated tiny numbers (installment counts, percentages, etc.).
      if (base > 0 && cents < base * 0.05) return;

      // Customer-facing discount/sale price should not be materially above base price.
      // A 2% tolerance handles rounding and tax/currency display differences.
      if (base > 0 && cents > base * 1.02) return;

      output.push(Math.round(cents));
    }

    isVisible(node) {
      if (!(node instanceof Element)) return false;
      const style = window.getComputedStyle(node);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && node.getClientRects().length > 0;
    }

    extractPrices(text) {
      const source = String(text || '').replace(/\u00a0/g, ' ');
      const matches = source.match(/(?:LKR\s*|Rs\.?\s*|₨\s*)?\d[\d,]*(?:\.\d{1,2})?/gi) || [];
      const results = [];

      matches.forEach((match) => {
        const hadCurrency = /LKR|Rs\.?|₨/i.test(match);
        const numeric = match.replace(/LKR|Rs\.?|₨/gi, '').replace(/,/g, '').trim();
        const number = Number(numeric);
        if (!Number.isFinite(number) || number <= 0) return;

        // Text without a currency marker is accepted only when it looks like a real price,
        // not a month count or percentage.
        if (!hadCurrency && number < 100) return;
        results.push(Math.round(number * 100));
      });

      return results;
    }

    updateCards(priceInCents) {
      this.querySelectorAll('[data-installment-card]').forEach((card) => {
        const months = Number(card.dataset.months || 0);
        const fee = Number(card.dataset.fee || 0);
        const min = Number(card.dataset.minCents || 0);
        const max = Number(card.dataset.maxCents || 0);
        if (!months) return;

        const total = priceInCents * ((100 + fee) / 100);
        const monthly = Math.round(total / months);
        const amountNode = card.querySelector('[data-installment-amount]');
        if (amountNode) amountNode.textContent = this.formatMoney(monthly);

        // Keep the plan visible even when discounted products move below/above a provider
        // limit. The card remains readable and clearly indicates eligibility instead of
        // disappearing from discounted products.
        const eligible = (!min || priceInCents >= min) && (!max || priceInCents <= max);
        card.classList.toggle('is-ineligible', !eligible);
        const message = card.querySelector('[data-installment-ineligible]');
        if (message) {
          message.hidden = eligible;
          if (!eligible) {
            if (min && priceInCents < min) message.textContent = `Available from ${this.formatMoney(min)}`;
            else if (max && priceInCents > max) message.textContent = `Available up to ${this.formatMoney(max)}`;
            else message.textContent = 'Not available for this price';
          }
        }
      });
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-LK', {
          style: 'currency',
          currency: 'LKR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(cents / 100).replace('LKR', 'Rs');
      } catch (error) {
        return `Rs ${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      }
    }
  }

  customElements.define('installment-plans', InstallmentPlans);
}
